import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_90b4c8a6-2372-4e9e-bd52-ac51039d9d6b/artifacts/l6uwsqcm_logo_bravo_cinemastreaming.png";

const CinemaLoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });
  
  // Password reset state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/cinemas/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      setLoading(false);
      let data;
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        data = { detail: xhr.responseText };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        localStorage.setItem('cinema_session', JSON.stringify(data));
        const pendingContentId = sessionStorage.getItem('pending_booking_content');
        if (pendingContentId) {
          sessionStorage.removeItem('pending_booking_content');
          navigate('/cinema/booking', { state: { contentId: pendingContentId } });
        } else {
          navigate('/cinema/booking');
        }
      } else {
        let errorMessage = data?.detail || 'Error al iniciar sesión';
        if (typeof errorMessage === 'string') {
          if (errorMessage.toLowerCase().includes('pending')) {
            errorMessage = 'Tu solicitud de registro está pendiente de aprobación. Te contactaremos pronto.';
          } else if (errorMessage.toLowerCase().includes('rejected')) {
            errorMessage = 'Tu solicitud de registro ha sido rechazada. Contacta con soporte para más información.';
          } else if (errorMessage.toLowerCase().includes('invalid credentials')) {
            errorMessage = 'Email o contraseña incorrectos';
          } else if (errorMessage.toLowerCase().includes('inactive')) {
            errorMessage = 'Tu cuenta ha sido desactivada. Contacta con soporte para más información.';
          }
        }
        setError(errorMessage);
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Error de conexión. Por favor, inténtalo de nuevo.');
    };
    
    xhr.send(JSON.stringify(formData));
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Por favor, introduce tu email');
      return;
    }

    setResetLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });

      if (res.ok) {
        setResetSent(true);
        toast.success('Email enviado correctamente');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Error al enviar el email');
      }
    } catch (err) {
      toast.error('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetSent(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="mb-8">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" /><span>Volver</span>
          </Link>
          <img src={LOGO_URL} alt="Bravo Cinemastreaming" className="h-12 w-auto mb-6" />
          <h1 className="text-white text-2xl font-bold mb-2">Acceso para Cines</h1>
          <p className="text-white/60">Inicia sesión para gestionar tus reservas y solicitar proyecciones.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#141414] rounded-xl p-6 space-y-6">
          {error && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>}
          <div>
            <label className="block text-white/60 text-sm mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#e4002b]" placeholder="email@ejemplo.com" required />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-white/60 text-sm">Contraseña</label>
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-[#e4002b] text-sm hover:underline"
                data-testid="forgot-password-link"
              >
                ¿No recuerdas tu contraseña?
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full pl-11 pr-11 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#e4002b]" placeholder="Tu contraseña" required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors disabled:opacity-50">
            {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </button>
          <div className="text-center pt-4 border-t border-white/10">
            <p className="text-white/50 text-sm">¿No tienes cuenta? <Link to="/cinema/register" className="text-[#e4002b] hover:underline">Registra tu cine</Link></p>
          </div>
        </form>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
          <div className="bg-[#141414] rounded-xl p-6 max-w-md w-full border border-white/10">
            {resetSent ? (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-white text-xl font-bold mb-2">¡Email enviado!</h3>
                <p className="text-white/60 mb-6">
                  Hemos enviado un enlace de recuperación a <strong className="text-white">{resetEmail}</strong>. 
                  Por favor, revisa tu bandeja de entrada (y spam) y sigue las instrucciones.
                </p>
                <button
                  onClick={closeResetModal}
                  className="px-6 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors"
                >
                  Volver al login
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-white text-xl font-bold mb-2">Recuperar contraseña</h3>
                <p className="text-white/60 mb-6">
                  Introduce tu email y te enviaremos un enlace para restablecer tu contraseña.
                </p>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#e4002b]"
                        placeholder="tu@email.com"
                        required
                        data-testid="reset-email-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeResetModal}
                      className="flex-1 px-4 py-3 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="flex-1 px-4 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="send-reset-btn"
                    >
                      {resetLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        'Enviar enlace'
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CinemaLoginPage;
